import React from "react";
import { useSelector } from "react-redux";
import { useRoleViewEffectiveCurrentUser } from "../contexts/RoleViewOverrideContext";

/** Role action check; `*` in `can` grants all actions (matches backend-style wildcards). */
export function roleCanAllows(can, action) {
  if (!action) return true;
  if (!Array.isArray(can)) return false;
  return can.includes("*") || can.includes(action);
}

// Define all available modules in Promang (must match backend)
export const AVAILABLE_MODULES = [
  "phone",
  "calendar",
  "customers",
  "customer-handler",
  "projects",
  "projects_work-orders",
  "hours",
  "service",
  "quality",
  "quality_checklists",
  "quality_deviations",
  "products",
  "cases",
  "driving",
  "tools",
  "resource_hub",
  "templates",
  "website",
  "offers",
  "orders",
  "inventory",
  "absences",
  "meeting_minutes",
  "drawings",
  "accounting",
  "hse_ks",
  "crew_scheduling",
  "signing",
  "payslips",
];

// Map specific roles to their parent feature module
// This allows us to check if the feature module is active before checking user roles
const ROLE_TO_FEATURE_MODULE = {
  "service_reports": "service",
  "service_agreements": "service",
  "service_settings": "service",
  "service": "service",
  "quality_checklists": "quality",
  "quality_deviations": "quality",
  "quality_settings": "quality",
  "quality": "quality",
  "hse_qualities": "quality",
  "hse_instructions": "quality",
  "hse_qualities_manage": "quality",
  "hse_instructions_manage": "quality",
  "chemical_inventory": "quality",
  "customer_settings": "customers",
  "customers": "customers",
  "customer-handler": "customer-handler",
  "contacts": "customers",
  "product_settings": "products",
  "products": "products",
  "hour_settings": "hours",
  "work_schedule": "hours",
  "hours_monthly_overview": "hours",
  "hours": "hours",
  "hours_approval": "hours",
  "hours_register": "absences",
  "project_settings": "projects",
  "projects": "projects",
  "projects_plan": "projects",
  "projects_accounting": "projects",
  "crew_scheduling": "crew_scheduling",
  "signing": "signing",
  "payslips": "payslips",
  "payslips_admin": "payslips",
  "projects_work-orders": "projects_work-orders",
  "inventory_settings": "inventory",
  "inventory": "inventory",
  "counts": "inventory",
  "order_settings": "orders",
  "orders": "orders",
  "offers_settings": "offers",
  "offers": "offers",
  "cases": "cases",
  "driving": "driving",
  "driving_trips": "driving",
  "driving_drivers": "driving",
  "driving_vehicles": "driving",
  "vehicles": "driving",
  "tools": "tools",
  "resource_hub": "resource_hub",
  "faq": "resource_hub",
  "templates": "templates",
  "website": "website",
  "webpage": "website",
  "absences": "absences",
  "absence": "absences",
  "absence_settings": "absences",
  "meeting_minutes": "meeting_minutes",
  "meeting_minutes_templates": "meeting_minutes",
  "drawings": "drawings",
  "accounting": "accounting",
  // HSE/KS module mappings
  "hse_ks": "hse_ks",
  "hse_ks_events": "hse_ks",
  "hse_ks_risk": "hse_ks",
  "hse_ks_documents": "hse_ks",
  "hse_ks_documents_approve": "hse_ks",
  "hse_ks_competency": "hse_ks",
  "hse_ks_competency_manage": "hse_ks",
  "hse_ks_evaluation": "hse_ks",
  "hse_ks_settings": "hse_ks",
  // Add more mappings as needed
};

/** Resolve `roles` array or single `role` for guards (sidebar, RoleOnly, routes). */
export function roleNamesFromProps({ role, roles }) {
  if (Array.isArray(roles) && roles.length > 0) return roles;
  if (role) return [role];
  return [];
}

/**
 * Full «Vehicles» list / fleet vehicle management (not sjåfør-only with driving + driving_trips).
 * Role name array from the current user.
 */
export function userHasDrivingFleetVehicleListAccess(roleNames) {
  if (!Array.isArray(roleNames)) return false;
  if (roleNames.includes("admin")) return true;
  if (roleNames.includes("driving_vehicles")) return true;
  if (roleNames.includes("driving_drivers")) return true;
  if (roleNames.includes("driving") && !roleNames.includes("driving_trips")) return true;
  return false;
}

/** When company uses active_modules, HSE/KS and quality can both unlock the same product surface. */
export function activeModulesAllowRole(roleName, activeModules) {
  if (!Array.isArray(activeModules)) return false;
  const featureModule = ROLE_TO_FEATURE_MODULE[roleName] || roleName;
  let candidates = [featureModule];
  if (featureModule === "hse_ks" || (typeof roleName === "string" && roleName.startsWith("hse_ks"))) {
    candidates = ["hse_ks", "quality"];
  } else if (roleName === "quality") {
    candidates = ["quality", "hse_ks"];
  }
  return candidates.some((m) => activeModules.includes(m));
}

/** Same rules as useCompanyHasModule — safe inside useMemo and for admin gating. */
export function companyHasActiveFeature(currentCompany, moduleName) {
  if (!moduleName) return true;
  const activeModules = currentCompany?.active_modules;
  const hasActiveModules =
    activeModules !== undefined && activeModules !== null;

  if (
    moduleName === "company" ||
    moduleName === "users" ||
    moduleName === "accounting"
  ) {
    return true;
  }

  if (!hasActiveModules) {
    return true;
  }

  if (moduleName === "hse_ks" || moduleName === "quality") {
    return (
      Array.isArray(activeModules) &&
      (activeModules.includes("hse_ks") || activeModules.includes("quality"))
    );
  }
  const featureModule = ROLE_TO_FEATURE_MODULE[moduleName] || moduleName;
  return Array.isArray(activeModules) && activeModules.includes(featureModule);
}

const BYPASS_MODULE_ROLES = ["company", "users", "admin", "accounting"];

/**
 * Shared guard for sidebar (RoleOnly), deep links (DashboardRoleRoute), and hooks.
 * Admins still respect `currentCompany.active_modules` (CompanyFeature); they do not need every granular role name on their JWT.
 */
export function userHasDashboardAccess({
  currentUser,
  currentCompany,
  role,
  roles,
  feature,
  action,
  /** If set (e.g. `["read","edit"]`), user needs any one action in `can`. Overrides single `action`. */
  actions,
  requireAdmin,
  requireAllRoles,
  drivingFleetVehiclesList,
}) {
  const roleNames = roleNamesFromProps({ role, roles });
  if (roleNames.length === 0 && !drivingFleetVehiclesList) return true;
  if (!currentUser?.roles) return false;

  const names = currentUser.roles.map((r) => r.name);
  const isAdmin = names.includes("admin");

  if (requireAdmin && !isAdmin) return false;

  if (drivingFleetVehiclesList) {
    const activeModules = currentCompany?.active_modules;
    const hasActiveModules =
      activeModules !== undefined && activeModules !== null;
    if (hasActiveModules) {
      const moduleOk = ["driving_vehicles", "driving", "driving_drivers"].some(
        (n) => activeModulesAllowRole(n, activeModules)
      );
      if (!moduleOk) return false;
    }
    return userHasDrivingFleetVehicleListAccess(names);
  }

  const activeModules = currentCompany?.active_modules;
  const hasActiveModules =
    activeModules !== undefined && activeModules !== null;

  if (feature && !companyHasActiveFeature(currentCompany, feature)) {
    return false;
  }

  if (hasActiveModules) {
    const allBypass = roleNames.every((n) => BYPASS_MODULE_ROLES.includes(n));
    if (!allBypass) {
      const anyModuleOk = roleNames.some((n) => {
        if (BYPASS_MODULE_ROLES.includes(n)) return true;
        return activeModulesAllowRole(n, activeModules);
      });
      if (!anyModuleOk) return false;
    }
  }

  if (roleNames.includes("accounting")) return false;

  if (isAdmin && !requireAllRoles) return true;

  if (roleNames.length === 1 && roleNames[0] === "hours_monthly_overview") {
    return (
      currentCompany?.hours_handler_id != null &&
      String(currentCompany.hours_handler_id) === String(currentUser?.id)
    );
  }

  const actionChecks =
    Array.isArray(actions) && actions.length > 0
      ? actions
      : action
        ? [action]
        : [];

  const roleAllows = (rn) => {
    const userRole = currentUser?.roles.find((r) => r.name === rn);
    if (!userRole) return false;
    return (
      actionChecks.length === 0 ||
      actionChecks.some((a) => roleCanAllows(userRole.can, a))
    );
  };

  if (requireAllRoles) {
    return roleNames.every(roleAllows);
  }

  for (const rn of roleNames) {
    if (roleAllows(rn)) return true;
  }

  return false;
}

const RoleOnly = ({
  role,
  roles,
  feature,
  action,
  actions,
  requireAdmin,
  requireAllRoles,
  children,
  placeholder = null,
  drivingFleetVehiclesList,
}) => {
  const currentUser = useRoleViewEffectiveCurrentUser();
  const currentCompany = useSelector((state) => state.session.currentCompany);

  const roleNames = roleNamesFromProps({ role, roles });
  if (roleNames.length === 0 && !drivingFleetVehiclesList) return children;

  if (!currentUser?.roles) {
    return placeholder;
  }

  const ok = userHasDashboardAccess({
    currentUser,
    currentCompany,
    role,
    roles,
    feature,
    action,
    actions,
        requireAdmin,
        requireAllRoles,
    drivingFleetVehiclesList,
  });
  return ok ? children : placeholder;
};

export default RoleOnly;

// A function that return a boolean value if role and action is confirmed like the component above
export const useUserHasAccess = ({ role, roles, feature, action, actions, requireAdmin, requireAllRoles }) => {
  const currentUser = useRoleViewEffectiveCurrentUser();
  const currentCompany = useSelector((state) => state.session.currentCompany);

  const roleNames = roleNamesFromProps({ role, roles });
  if (roleNames.length === 0) return true;

  if (!currentUser?.roles) {
    return false;
  }

  return userHasDashboardAccess({
    currentUser,
    currentCompany,
    role,
    roles,
    feature,
    action,
    actions,
    requireAdmin,
    requireAllRoles,
    drivingFleetVehiclesList: false,
  });
};

export const useUserHasDrivingFleetVehicleListAccess = () => {
  const currentUser = useRoleViewEffectiveCurrentUser();
  const currentCompany = useSelector((state) => state.session.currentCompany);

  if (!currentUser?.roles) return false;

  const names = currentUser.roles.map((r) => r.name);
  const activeModules = currentCompany?.active_modules;
  const hasActiveModules =
    activeModules !== undefined && activeModules !== null;
  if (hasActiveModules) {
    const moduleOk = ["driving_vehicles", "driving", "driving_drivers"].some((n) =>
      activeModulesAllowRole(n, activeModules)
    );
    if (!moduleOk) return false;
  }
  if (names.includes("admin")) return true;
  return userHasDrivingFleetVehicleListAccess(names);
};

function companyHasHoursModule(currentCompany) {
  const activeModules = currentCompany?.active_modules;
  const hasActiveModules =
    activeModules !== undefined && activeModules !== null;
  if (!hasActiveModules) return true;
  return activeModulesAllowRole("hours", activeModules);
}

export const useCanManageHoursMonthlyOverview = () => {
  const currentUser = useRoleViewEffectiveCurrentUser();
  const currentCompany = useSelector((state) => state.session.currentCompany);

  if (!companyHasHoursModule(currentCompany)) return false;

  const isAdmin = currentUser?.roles?.some((r) => r.name === "admin");
  if (isAdmin) {
    return true;
  }

  return (
    currentCompany?.hours_handler_id != null &&
    String(currentCompany.hours_handler_id) === String(currentUser?.id)
  );
};

export const useCanApproveHours = () => {
  const currentUser = useRoleViewEffectiveCurrentUser();
  const currentCompany = useSelector((state) => state.session.currentCompany);

  if (!companyHasHoursModule(currentCompany)) return false;

  const isAdmin = currentUser?.roles?.some((r) => r.name === "admin");
  if (isAdmin) {
    return true;
  }

  return currentUser?.roles?.some((r) => r.name === "hours_approval") || false;
};

/** Matches API bulk_reassign: admin, hours_approval, or edit_hours */
export const useCanBulkReassignHours = () => {
  const currentUser = useRoleViewEffectiveCurrentUser();
  const currentCompany = useSelector((state) => state.session.currentCompany);
  if (!companyHasHoursModule(currentCompany)) return false;

  const roles = currentUser?.roles || [];
  return (
    roles.some((r) => r.name === "admin") ||
    roles.some((r) => r.name === "hours_approval") ||
    roles.some((r) => r.name === "edit_hours")
  );
};

// Rolle- og statusmatrise
const roleMatrix = {
  workOrder: {
    RESPONSIBLE: [
      "edit",
      "view",
      "execute",
      "approve",
      "finish",
      "add_note",
      "time_register",
      "item_consumption",
      "work_notes",
      "price_estimate",
    ],
    /** Ansvarlig utfører — samme feltrettigheter som utfører (API-rolle RESPONSIBLE_EXECUTOR). */
    RESPONSIBLE_EXECUTOR: [
      "view",
      "execute",
      "finish",
      "add_note",
      "time_register",
      "item_consumption",
      "work_notes",
    ],
    EXECUTOR: [
      "view",
      "execute",
      "finish",
      "add_note",
      "time_register",
      "item_consumption",
      "work_notes",
    ],
    OBSERVER: ["view", "add_note"],
    ADMIN: ["*"],
  },
  project: {
    RESPONSIBLE: [
      "edit",
      "view",
      "manage_participants",
      "manage_folders",
      "create_work_orders",
      "create_reports",
      "create_orders",
      "create_service_agreements",
      "add_notes",
      "manage_budget",
      "approve_work_orders",
      "read_item_lists",
      "create_item_lists",
    ],
    /** Utførende: detaljer, aktiviteter, mapper, egne timer/vareliste — ikke AO, rapporter, timekost, etc. */
    EXECUTOR: [
      "view",
      "time_register",
      "read_item_lists",
      "create_item_lists",
    ],
    OBSERVER: ["view", "add_notes"],
    admin: ["*"], // Note: lowercase 'admin' for admin users
  },
  service: {
    RESPONSIBLE: [
      "create",
      "edit",
      "view",
      "delete",
      "approve",
      "manage",
      "print",
    ],
    EXECUTOR: ["create", "edit", "view", "print"],
    OBSERVER: ["view"],
    admin: ["*"],
  },
  // ... flere moduler om ønskelig
};

const statusMatrix = {
  closed: ["view"],
  // REJECTED: ["view"],
  approved: ["view"],
  // draft/ongoing: field staff (EXECUTOR etc.) must add notes and consumption — use role matrix, not view-only.
  finished: ["view"],
  processing: ["view"],
  processed: ["view"],
  cancelled: ["view"],
  // ... flere statuser om ønskelig
};

export function hasPermission(module, role, action, status = null) {
  // Admin har alltid tilgang
  if (role === "ADMIN") return { allowed: true };

  // Hvis status er lukket, sjekk status-matrisen
  if (status && statusMatrix[status]) {
    if (!statusMatrix[status].includes(action)) {
      return { allowed: false, reason: "closed" };
    }
  }

  // Sjekk rolle-matrisen
  const allowed = roleMatrix[module]?.[role] || [];
  return { allowed: allowed.includes(action) || allowed.includes("*") };
}

// Custom hook for work order permissions
export function useWorkOrderPermissions(workOrder) {
  const { useMemo } = React;
  const currentUser = useRoleViewEffectiveCurrentUser();
  const currentCompany = useSelector((state) => state.session.currentCompany);

  return useMemo(() => {
    if (!workOrder) return {};

    if (!companyHasActiveFeature(currentCompany, "projects_work-orders")) {
      return {
        canEdit: false,
        canView: false,
        canExecute: false,
        canApprove: false,
        canFinishWorkOrder: false,
        canAddNote: false,
        canTimeRegister: false,
        canItemConsumption: false,
        canWorkNotes: false,
        canPriceEstimate: false,
        isClosed: false,
        isReadOnly: true,
        isAdmin: false,
      };
    }

    const role = workOrder.role_for_current_user;
    const status = workOrder.status;

    // Check if user is admin
    const isAdmin = currentUser?.roles?.some((r) => r.name === "admin");

    return {
      canEdit:
        isAdmin || hasPermission("workOrder", role, "edit", status).allowed,
      canView:
        isAdmin || hasPermission("workOrder", role, "view", status).allowed,
      canExecute:
        isAdmin || hasPermission("workOrder", role, "execute", status).allowed,
      canApprove:
        isAdmin || hasPermission("workOrder", role, "approve", status).allowed,
      canAddNote:
        isAdmin || hasPermission("workOrder", role, "add_note", status).allowed,
      canTimeRegister:
        isAdmin ||
        hasPermission("workOrder", role, "time_register", status).allowed,
      canItemConsumption:
        isAdmin ||
        hasPermission("workOrder", role, "item_consumption", status).allowed,
      canWorkNotes:
        isAdmin ||
        hasPermission("workOrder", role, "work_notes", status).allowed,
      canPriceEstimate:
        isAdmin ||
        hasPermission("workOrder", role, "price_estimate", status).allowed,
      canFinishWorkOrder:
        isAdmin ||
        hasPermission("workOrder", role, "finish", status).allowed,
      isClosed:
        hasPermission("workOrder", role, "edit", status).reason === "closed",
      isReadOnly:
        !isAdmin &&
        hasPermission("workOrder", role, "edit", status).reason === "closed",
      isAdmin: isAdmin,
    };
  }, [
    currentCompany,
    currentUser?.roles,
    workOrder?.role_for_current_user,
    workOrder?.status,
  ]);
}

/** Customer invoices, supplier invoices, billing plan, project summary (same gate as API `projects_accounting`). */
export function useProjectAccountingAccess() {
  return useUserHasAccess({ roles: ["projects_accounting"] });
}

// Custom hook for project permissions
export function useProjectPermissions(project) {
  const { useMemo } = React;
  const currentUser = useSelector((state) => state.session.currentUser);
  const currentCompany = useSelector((state) => state.session.currentCompany);

  return useMemo(() => {
    if (!project) return {};

    if (!companyHasActiveFeature(currentCompany, "projects")) {
      const deny = {
        participantRole: project.role_for_current_user || null,
        isExecutorParticipant: false,
        canEdit: false,
        canView: false,
        canRead: false,
        can: false,
        canManageParticipants: false,
        canManageFolders: false,
        canReadFolders: false,
        canCreateWorkOrders: false,
        canCreateReports: false,
        canCreateOrders: false,
        canCreateServiceAgreements: false,
        canAddNotes: false,
        canManageBudget: false,
        canApproveWorkOrders: false,
        canTimeRegister: false,
        canReadItemLists: false,
        canCreateItemList: false,
        canMergeItemListsToOrder: false,
        canSeeProjectHourlyCosts: false,
        isFinished: project.finished,
        isActive: project.active,
        isReadOnly: true,
        isAdmin: false,
      };
      return deny;
    }

    const role = project.role_for_current_user;
    const status = project.status;

    // Check if user is admin
    const isAdmin = currentUser?.roles?.some((r) => r.name === "admin");

    const canViewProject =
      isAdmin || hasPermission("project", role, "view", status).allowed;

    const isExecutorParticipant = !isAdmin && role === "EXECUTOR";

    const canReadItemLists =
      isAdmin ||
      hasPermission("project", role, "read_item_lists", status).allowed ||
      hasPermission("project", role, "create_item_lists", status).allowed ||
      hasPermission("project", role, "create_orders", status).allowed ||
      hasPermission("project", role, "edit", status).allowed;

    const canCreateItemList =
      isAdmin ||
      hasPermission("project", role, "create_item_lists", status).allowed ||
      hasPermission("project", role, "create_orders", status).allowed ||
      hasPermission("project", role, "edit", status).allowed;

    const canMergeItemListsToOrder =
      isAdmin ||
      hasPermission("project", role, "create_orders", status).allowed ||
      hasPermission("project", role, "edit", status).allowed;

    const canSeeProjectHourlyCosts =
      isAdmin ||
      hasPermission("project", role, "edit", status).allowed ||
      hasPermission("project", role, "manage_budget", status).allowed;

    return {
      participantRole: role || null,
      isExecutorParticipant,
      canEdit:
        isAdmin || hasPermission("project", role, "edit", status).allowed,
      canView: canViewProject,
      /** Alias used across project pages (same as `canView`). */
      canRead: canViewProject,
      can:
        isAdmin || hasPermission("project", role, "view", status).allowed,
      canManageParticipants:
        isAdmin ||
        hasPermission("project", role, "manage_participants", status).allowed,
      canManageFolders:
        isAdmin ||
        hasPermission("project", role, "manage_folders", status).allowed,
      canReadFolders:
        isAdmin || hasPermission("project", role, "view", status).allowed,
      canCreateWorkOrders:
        isAdmin ||
        hasPermission("project", role, "create_work_orders", status).allowed,
      canCreateReports:
        isAdmin ||
        hasPermission("project", role, "create_reports", status).allowed,
      canCreateOrders:
        isAdmin ||
        hasPermission("project", role, "create_orders", status).allowed,
      canCreateServiceAgreements:
        isAdmin ||
        hasPermission("project", role, "create_service_agreements", status)
          .allowed,
      canAddNotes:
        isAdmin || hasPermission("project", role, "add_notes", status).allowed,
      canManageBudget:
        isAdmin ||
        hasPermission("project", role, "manage_budget", status).allowed,
      canApproveWorkOrders:
        isAdmin ||
        hasPermission("project", role, "approve_work_orders", status).allowed,
      canTimeRegister:
        isAdmin ||
        hasPermission("project", role, "time_register", status).allowed,
      canReadItemLists,
      canCreateItemList,
      canMergeItemListsToOrder,
      canSeeProjectHourlyCosts,
      isFinished: project.finished,
      isActive: project.active,
      isReadOnly:
        !isAdmin && !hasPermission("project", role, "edit", status).allowed,
      isAdmin: isAdmin,
    };
  }, [
    currentCompany,
    currentUser?.roles,
    project?.role_for_current_user,
    project?.status,
    project?.finished,
    project?.active,
  ]);
}

// Helper function to check if user has access to a specific module within a project
export function useModuleAccess(module, project = null) {
  const { useMemo } = React;
  const currentUser = useRoleViewEffectiveCurrentUser();
  const currentCompany = useSelector((state) => state.session.currentCompany);

  return useMemo(() => {
    if (!companyHasActiveFeature(currentCompany, module)) {
      return {
        canRead: false,
        canCreate: false,
        canEdit: false,
        canDelete: false,
        isAdmin: false,
      };
    }

    const isAdmin = currentUser?.roles?.some((r) => r.name === "admin");
    if (isAdmin) {
      return {
        canRead: true,
        canCreate: true,
        canEdit: true,
        canDelete: true,
        isAdmin: true,
      };
    }

    // Check for granular module roles
    const moduleRole = currentUser?.roles?.find((r) => r.name === module);
    const moduleCreateRole = currentUser?.roles?.find(
      (r) => r.name === `${module}_create`
    );
    const moduleEditRole = currentUser?.roles?.find(
      (r) => r.name === `${module}_edit`
    );
    const moduleDeleteRole = currentUser?.roles?.find(
      (r) => r.name === `${module}_delete`
    );

    // If user has any module-specific role, check granular permissions
    if (moduleRole || moduleCreateRole || moduleEditRole || moduleDeleteRole) {
      return {
        canRead: moduleRole
          ? moduleRole.can.includes("read") || moduleRole.can.includes("*")
          : false,
        canCreate: moduleCreateRole
          ? moduleCreateRole.can.includes("create") ||
            moduleCreateRole.can.includes("*")
          : false,
        canEdit: moduleEditRole
          ? moduleEditRole.can.includes("edit") ||
            moduleEditRole.can.includes("*")
          : false,
        canDelete: moduleDeleteRole
          ? moduleDeleteRole.can.includes("delete") ||
            moduleDeleteRole.can.includes("*")
          : false,
        isAdmin: false,
      };
    }

    // If no module role at all, deny all access (don't fall back to project roles)
    return {
      canRead: false,
      canCreate: false,
      canEdit: false,
      canDelete: false,
      isAdmin: false,
    };
  }, [
    currentCompany,
    currentUser?.roles,
    project?.role_for_current_user,
    module,
  ]);
}

// Specific hooks for common modules
export function useProjectServiceReportsAccess(project = null) {
  return useModuleAccess("service", project);
}

export function useProjectServiceAgreementsAccess(project = null) {
  return useModuleAccess("service", project);
}

export function useProjectWorkOrdersAccess(project = null) {
  return useModuleAccess("projects_work-orders", project);
}

export function useProjectQualityChecklistsAccess(project = null) {
  return useModuleAccess("quality_checklists", project);
}

export function useProjectQualityDeviationsAccess(project = null) {
  return useModuleAccess("quality_deviations", project);
}

// Helper function to check if company has an active module
export const useCompanyHasModule = (moduleName) => {
  const currentCompany = useSelector((state) => state.session.currentCompany);
  return companyHasActiveFeature(currentCompany, moduleName);
};
